import { apiClient } from './../client';
import { API_ENDPOINTS } from './../constant/endpoints';
import type { IApiResponse, IPaginatedResponse } from './../types';
import type {
    IBlockedNumber,
    IBlockNumberInput,
    ICall,
    ICallAnalytics,
    ICallAnalyticsQuery,
    ICallDetail,
    ICallHistoryRow,
    IGreetingUploadUrl,
    IGreetingUploadUrlInput,
    IInitiateCallInput,
    IInitiateCallResult,
    IListBlockedNumbersQuery,
    IListCallsQuery,
    IListRecordingsQuery,
    IMintedVoiceToken,
    IPresignedRecordingUrl,
    ITransferCallInput,
    IUpdateBlockedNumberInput,
    IUpdateCallDispositionInput,
    IUpsertVoiceNumberConfigInput,
    IVoiceNumberConfig,
    IBusinessNumber,
    IVoiceNumberSummary,
    IVoiceGreeting,
    IVoicePresenceEntry,
    IVoiceRecording,
    IVoiceSettingsState,
    VoicePresenceStatus,
} from '@/features/voice/types';

export const voiceApi = {
    // ── Settings ─────────────────────────────────────────────────────────
    getSettings: () =>
        apiClient
            .get<IApiResponse<IVoiceSettingsState>>(API_ENDPOINTS.VOICE.SETTINGS)
            .then((r) => r.data.data),

    enable: () =>
        apiClient
            .post<IApiResponse<{ status: string }>>(API_ENDPOINTS.VOICE.SETTINGS + '/enable')
            .then((r) => r.data.data),

    updateSettings: (data: {
        defaultVoiceIdentityId?: string | null;
        defaultVoicemailGreeting?: IVoiceGreeting | null;
        unavailableMessage?: IVoiceGreeting | null;
    }) =>
        apiClient
            .patch<IApiResponse<null>>(API_ENDPOINTS.VOICE.SETTINGS, data)
            .then((r) => r.data.data),

    disable: () =>
        apiClient.delete<IApiResponse<null>>(API_ENDPOINTS.VOICE.SETTINGS).then((r) => r.data.data),

    // ── Token ────────────────────────────────────────────────────────────
    mintToken: () =>
        apiClient
            .post<IApiResponse<IMintedVoiceToken>>(API_ENDPOINTS.VOICE.TOKEN)
            .then((r) => r.data.data),

    // ── Presence ─────────────────────────────────────────────────────────
    getPresence: () =>
        apiClient
            .get<IApiResponse<IVoicePresenceEntry[]>>(API_ENDPOINTS.VOICE.PRESENCE)
            .then((r) => r.data.data),

    setMyPresence: (status: VoicePresenceStatus) =>
        apiClient
            .put<IApiResponse<null>>(API_ENDPOINTS.VOICE.PRESENCE_ME, { status })
            .then((r) => r.data.data),

    // ── Numbers / config ─────────────────────────────────────────────────
    listNumbers: () =>
        apiClient
            .get<IApiResponse<IVoiceNumberSummary[]>>(API_ENDPOINTS.VOICE.NUMBERS)
            .then((r) => r.data.data),

    /** Numbers where the signed-in agent is a ring target (caller-ID choices). */
    listMyNumbers: () =>
        apiClient
            .get<IApiResponse<IVoiceNumberSummary[]>>(API_ENDPOINTS.VOICE.NUMBERS_MINE)
            .then((r) => r.data.data),

    getNumberConfig: (channelIdentityId: string) =>
        apiClient
            .get<
                IApiResponse<IVoiceNumberConfig>
            >(API_ENDPOINTS.VOICE.NUMBER_CONFIG(channelIdentityId))
            .then((r) => r.data.data),

    upsertNumberConfig: (channelIdentityId: string, data: IUpsertVoiceNumberConfigInput) =>
        apiClient
            .put<IApiResponse<null>>(API_ENDPOINTS.VOICE.NUMBER_CONFIG(channelIdentityId), data)
            .then((r) => r.data.data),

    // ── Greeting audio ───────────────────────────────────────────────────
    getGreetingUploadUrl: (data: IGreetingUploadUrlInput) =>
        apiClient
            .post<IApiResponse<IGreetingUploadUrl>>(API_ENDPOINTS.VOICE.GREETING_UPLOAD_URL, data)
            .then((r) => r.data.data),

    getGreetingPlaybackUrl: (storageKey: string) =>
        apiClient
            .get<IApiResponse<IPresignedRecordingUrl>>(API_ENDPOINTS.VOICE.GREETING_PLAYBACK_URL, {
                params: { storageKey },
            })
            .then((r) => r.data.data),

    // ── Calls — commands ─────────────────────────────────────────────────
    initiateCall: (data: IInitiateCallInput) =>
        apiClient
            .post<IApiResponse<IInitiateCallResult>>(API_ENDPOINTS.VOICE.CALLS, data)
            .then((r) => r.data.data),

    endCall: (id: string) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.VOICE.CALL_END(id))
            .then((r) => r.data.data),

    holdCall: (id: string) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.VOICE.CALL_HOLD(id))
            .then((r) => r.data.data),

    resumeCall: (id: string) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.VOICE.CALL_RESUME(id))
            .then((r) => r.data.data),

    transferCall: (id: string, data: ITransferCallInput) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.VOICE.CALL_TRANSFER(id), data)
            .then((r) => r.data.data),

    /** Claim a caller off the hold queue; returns the leg the widget joins. */
    pickHeldCall: (id: string) =>
        apiClient
            .post<IApiResponse<IInitiateCallResult>>(API_ENDPOINTS.VOICE.CALL_PICK(id))
            .then((r) => r.data.data),

    updateDisposition: (id: string, data: IUpdateCallDispositionInput) =>
        apiClient
            .patch<IApiResponse<null>>(API_ENDPOINTS.VOICE.CALL_DISPOSITION(id), data)
            .then((r) => r.data.data),

    /** Link a just-ended call to a contact the agent saved from the wrap-up card. */
    linkContact: (id: string, data: { contactId: string }) =>
        apiClient
            .patch<IApiResponse<null>>(API_ENDPOINTS.VOICE.CALL_CONTACT(id), data)
            .then((r) => r.data.data),

    // ── Calls — queries ──────────────────────────────────────────────────
    /** The organization's own numbers, for the Calls "Business number" filter. */
    listBusinessNumbers: () =>
        apiClient
            .get<IApiResponse<IBusinessNumber[]>>(API_ENDPOINTS.VOICE.CALL_BUSINESS_NUMBERS)
            .then((r) => r.data.data),

    listCalls: (params?: IListCallsQuery) =>
        apiClient
            .get<
                IApiResponse<ICall[]> & { meta: IPaginatedResponse<ICall> }
            >(API_ENDPOINTS.VOICE.CALLS, { params })
            .then((r) => ({
                data: r.data.data,
                page: r.data.meta.page,
                limit: r.data.meta.limit,
                total: r.data.meta.total,
                totalPages: r.data.meta.totalPages,
            })),

    /** The Calls page (P9-8): team and AI-answered calls in one page, tagged `handledBy`. */
    listCallHistory: (params?: IListCallsQuery) =>
        apiClient
            .get<
                IApiResponse<ICallHistoryRow[]> & { meta: IPaginatedResponse<ICallHistoryRow> }
            >(API_ENDPOINTS.VOICE.CALLS, { params })
            .then((r) => ({
                data: r.data.data,
                page: r.data.meta.page,
                limit: r.data.meta.limit,
                total: r.data.meta.total,
                totalPages: r.data.meta.totalPages,
            })),

    /**
     * The Call History as CSV under the list's filters and sort — every
     * matching call, streamed server-side; `page`/`limit` are ignored there.
     */
    exportCallsCsv: (params?: IListCallsQuery): Promise<Blob> =>
        apiClient
            .get<Blob>(API_ENDPOINTS.VOICE.CALLS_EXPORT, { responseType: 'blob', params })
            .then((r) => r.data),

    getCallAnalytics: (params?: ICallAnalyticsQuery) =>
        apiClient
            .get<IApiResponse<ICallAnalytics>>(API_ENDPOINTS.VOICE.CALL_ANALYTICS, { params })
            .then((r) => r.data.data),

    getCallById: (id: string) =>
        apiClient
            .get<IApiResponse<ICallDetail>>(API_ENDPOINTS.VOICE.CALL_BY_ID(id))
            .then((r) => r.data.data),

    // ── Recordings ───────────────────────────────────────────────────────
    listRecordings: (params?: IListRecordingsQuery) =>
        apiClient
            .get<
                IApiResponse<IVoiceRecording[]> & { meta: IPaginatedResponse<IVoiceRecording> }
            >(API_ENDPOINTS.VOICE.RECORDINGS, { params })
            .then((r) => ({
                data: r.data.data,
                page: r.data.meta.page,
                limit: r.data.meta.limit,
                total: r.data.meta.total,
                totalPages: r.data.meta.totalPages,
            })),

    getRecordingUrl: (id: string) =>
        apiClient
            .get<IApiResponse<IPresignedRecordingUrl>>(API_ENDPOINTS.VOICE.RECORDING_URL(id))
            .then((r) => r.data.data),

    markRecordingHeard: (id: string) =>
        apiClient
            .patch<IApiResponse<null>>(API_ENDPOINTS.VOICE.RECORDING_HEARD(id))
            .then((r) => r.data.data),

    deleteRecording: (id: string) =>
        apiClient
            .delete<IApiResponse<null>>(API_ENDPOINTS.VOICE.RECORDING_BY_ID(id))
            .then((r) => r.data.data),

    // ── Blocked numbers ──────────────────────────────────────────────────
    listBlockedNumbers: (params?: IListBlockedNumbersQuery) =>
        apiClient
            .get<
                IApiResponse<IBlockedNumber[]> & { meta: IPaginatedResponse<IBlockedNumber> }
            >(API_ENDPOINTS.VOICE.BLOCKED_NUMBERS, { params })
            .then((r) => ({
                data: r.data.data,
                page: r.data.meta.page,
                limit: r.data.meta.limit,
                total: r.data.meta.total,
                totalPages: r.data.meta.totalPages,
            })),

    blockNumber: (data: IBlockNumberInput) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.VOICE.BLOCKED_NUMBERS, data)
            .then((r) => r.data.data),

    updateBlockedNumber: ({ id, data }: { id: string; data: IUpdateBlockedNumberInput }) =>
        apiClient
            .patch<IApiResponse<null>>(API_ENDPOINTS.VOICE.BLOCKED_NUMBER_BY_ID(id), data)
            .then((r) => r.data.data),

    unblockNumber: (id: string) =>
        apiClient
            .delete<IApiResponse<null>>(API_ENDPOINTS.VOICE.BLOCKED_NUMBER_BY_ID(id))
            .then((r) => r.data.data),

    // ── Contact preferred caller ID ──────────────────────────────────────
    setPreferredCallerId: (contactId: string, channelIdentityId: string | null) =>
        apiClient
            .put<IApiResponse<null>>(API_ENDPOINTS.CONTACTS.PREFERRED_CALLER_ID(contactId), {
                channelIdentityId,
            })
            .then((r) => r.data.data),
};
